import { useEffect, useState } from "react";
import type { ReactNode, CSSProperties } from "react";
import { OwcIconButton, OwcInput, OwcTypography, OwcProgressSpinner, OwcTooltip } from "@one/react";
import styles from "./InlineEditableField.module.scss";
import { DURATION } from "../../../constants";
// @ts-ignore
import { snackbarService } from "@one/web-components/dist/index.cjs.js";

type ValidationResult = string | null | undefined;

type DisplayFormatter = (value: string | number | null | undefined) => ReactNode;

type SaveHandler = (value: string) => void | Promise<void>;

type CancelHandler = () => void;

type EditStateHandler = () => void;

type OwcInputType = "text" | "password" | "email" | "number" | "url" | "tel" | "time";

type OwcTypographyVariant = 'subtitle2' | 'body2' | 'button' | 'caption' | 'subtitle1' | 'body1';

export interface InlineEditableFieldProps {
  label: string;
  value: string | number | null | undefined;
  placeholder?: string;
  helperText?: string;
  variant?: OwcTypographyVariant;
  allowEdit?: boolean;
  disabled?: boolean;
  inputType?: OwcInputType;
  onSave?: SaveHandler;
  onCancel?: CancelHandler;
  onEditStart?: EditStateHandler;
  onEditEnd?: EditStateHandler;
  validation?: (value: string) => ValidationResult;
  displayFormatter?: DisplayFormatter;
  className?: string;
  actionsClassName?: string;
  actionsStyle?: CSSProperties;
  containerStyle?: CSSProperties;
  maxDisplayLength?: number;
  showTooltip?: boolean;
}

const ensureString = (rawValue: string | number | null | undefined): string => {
  if (rawValue === null || rawValue === undefined) {
    return "";
  }
  return String(rawValue);
};

const InlineEditableField = ({
  label,
  value,
  placeholder,
  helperText,
  variant = "subtitle2",
  allowEdit = true,
  disabled = false,
  inputType = "text",
  onSave,
  onCancel,
  onEditStart,
  onEditEnd,
  validation,
  displayFormatter,
  className,
  actionsClassName,
  actionsStyle,
  containerStyle,
  maxDisplayLength = 100,
  showTooltip = true,
}: InlineEditableFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState<string>(ensureString(value));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditable = allowEdit && !disabled;

  useEffect(() => {
    if (!isEditing) {
      setDraftValue(ensureString(value));
    }
  }, [value, isEditing]);

  const getTruncatedValue = (val: string | number | null | undefined): string => {
    const stringValue = ensureString(val);
    if (!stringValue) return "-";

    if (stringValue.length > maxDisplayLength) {
      const charsToKeep = maxDisplayLength - 3;
      return stringValue.substring(0, charsToKeep) + "...";
    }

    return stringValue;
  };

  const formattedValue = (() => {
    if (displayFormatter) {
      const formatted = displayFormatter(value);
      if (typeof formatted === 'string') {
        return getTruncatedValue(formatted);
      }
      return formatted;
    }
    return getTruncatedValue(value);
  })();

  const shouldShowTooltip = (() => {
    if (!showTooltip) return false;
    const stringValue = ensureString(value);
    return stringValue.length > 0 && stringValue !== "-";
  })();

  const hasChanges = () => ensureString(value).trim() !== draftValue.trim();

  const resetState = () => {
    setDraftValue(ensureString(value));
    setError(null);
    setIsEditing(false);
    setSaving(false);
  };

  const handleEditClick = () => {
    if (!isEditable) return;
    setDraftValue(ensureString(value));
    setError(null);
    setIsEditing(true);
    onEditStart?.();
  };

  const handleCancel = () => {
    resetState();
    onCancel?.();
    onEditEnd?.();
  };

  const handleSave = async () => {
    const trimmedValue = draftValue.trim();

    if (validation) {
      const validationResult = validation(trimmedValue);
      if (typeof validationResult === "string" && validationResult) {
        setError(validationResult);
        return;
      }
    }

    if (!onSave) {
      resetState();
      onEditEnd?.();
      return;
    }

    try {
      setSaving(true);
      await onSave(trimmedValue);
      resetState();
      onEditEnd?.();
    } catch (err) {
      setSaving(false);
      const message = err instanceof Error ? err.message : "Failed to save value";
      try {
        snackbarService.show({
          message,
          type: "error",
          duration: DURATION,
        });
      } catch (_) { }
      setError(message);
      resetState();
      onEditEnd?.();
    }
  };

  return (
    <div className={className || styles.fieldContainer} style={containerStyle}>
      <div className={styles.headerRow}>
        <OwcTypography variant="caption" className={styles.label} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          {label}
        </OwcTypography>
        {isEditable && !isEditing && (
          <div className={actionsClassName || styles.actions} style={actionsStyle}>
            <OwcIconButton
              icon="edit"
              type="outlined"
              flat
              onClick={e => {
                e.stopPropagation?.();
                handleEditClick();
              }}
              style={{ color: "#0B41CD" }}
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </div>
        )}
        {isEditable && isEditing && (
          <div className={actionsClassName || styles.actions} style={actionsStyle}>
            {saving ? (
              <OwcProgressSpinner
                style={{ width: "24px", height: "24px" }}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            ) : (
              <>
                <OwcIconButton
                  icon="circle_clear"
                  type="filled"
                  flat
                  disabled={disabled || saving}
                  onClick={(e: any) => {
                    e.stopPropagation?.();
                    handleCancel();
                  }}
                  style={{ color: hasChanges() ? "#DC3545" : "#999999" }}
                  placeholder={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <OwcIconButton
                  icon="circle_confirm"
                  type="filled"
                  flat
                  disabled={disabled || saving || !hasChanges()}
                  onClick={(e: any) => {
                    e.stopPropagation?.();
                    handleSave();
                  }}
                  style={{ color: hasChanges() ? "#28A745" : "#999999" }}
                  placeholder={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              </>
            )}
          </div>
        )}
      </div>

      {isEditing ? (
        <div className={`${styles.editContent} ${saving ? styles.editContentSaving : ""}`}>
          <OwcInput
            type={inputType}
            value={draftValue}
            placeholder={placeholder}
            disabled={disabled || saving}
            onInputChange={(event: any) => {
              const nextValue = ensureString(event?.target?.value ?? "");
              setDraftValue(nextValue);
              if (error) setError(null);
            }}
            style={{ flex: 1, minHeight: '40px' }} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
        </div>
      ) : null}

      <div style={{ display: isEditing ? 'none' : 'block' }}>
        <div id={`inline-field-value-${label.replace(/\s+/g, '-').toLowerCase()}`}>
          <OwcTypography variant={variant} className={styles.value} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {formattedValue}
          </OwcTypography>
        </div>
        {shouldShowTooltip && (
          <OwcTooltip
            anchor={`inline-field-value-${label.replace(/\s+/g, '-').toLowerCase()}`}
            placement="bottom"
            placeholder={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          >
            {ensureString(value)}
          </OwcTooltip>
        )}
      </div>

      {(helperText || error) && (
        <OwcTypography variant="caption" className={styles.errorText} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          {error || helperText}
        </OwcTypography>
      )}
    </div>
  );
};

export default InlineEditableField;
